<!-- app-subdomain-creators\src\Components\CreatorAssignMemberTagModal.vue -->

<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog class="relative z-50" @close="handleClose">
      <!-- Overlay -->
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-75 transition-opacity backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed top-[20%] inset-x-0 z-10 w-screen">
        <div class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform rounded-lg bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-8"
            >
              <!-- Close X -->
              <button
                type="button"
                class="absolute top-4 right-4 rounded-full text-zinc-400 hover:text-zinc-600 focus:outline-none"
                @click="handleClose"
              >
                <XMarkIcon class="h-5 w-5" />
              </button>

              <!-- Header -->
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <TagIcon class="h-6 w-6 text-purple-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    class="text-xl font-semibold leading-6 text-gray-900"
                  >
                    Assign a new tag
                  </DialogTitle>
                  <p class="mt-1 text-sm text-gray-500">
                    Use an existing tag or create a new one
                    <span v-if="memberName">for<br>{{ memberName }}</span>?
                  </p>
                </div>
              </div>

              <!-- Body -->
              <div class="mt-6 space-y-5">
                <!-- Tagging action -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Use an existing tag or create a new one?
                  </label>
                  <div class="relative">
                    <!-- Pseudo-select button -->
                    <button
                      type="button"
                      class="flex w-full items-center justify-between rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
                      @click="toggleActionDropdown"
                    >
                      <span class="truncate">
                        {{ selectedActionLabel }}
                      </span>
                      <ChevronUpDownIcon class="w-4 h-4 text-zinc-400 flex-shrink-0" />
                    </button>

                    <!-- Action dropdown -->
                    <div
                      v-if="isActionDropdownOpen"
                      class="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg"
                    >
                      <ul class="py-1 text-sm">
                        <li>
                          <button
                            type="button"
                            class="flex w-full items-center px-3 py-2 text-left hover:bg-zinc-100"
                            @click="selectActionMode('existing')"
                          >
                            <span>Select an existing tag</span>
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            class="flex w-full items-center px-3 py-2 text-left hover:bg-zinc-100"
                            @click="selectActionMode('new')"
                          >
                            <span>Create a new tag</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Existing tag selector -->
                <div v-if="selectedMode === 'existing'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Select a tag
                  </label>

                  <div class="relative">
                    <!-- "Pseudo-select" button -->
                    <button
                      type="button"
                      class="flex w-full items-center justify-between rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
                      @click="toggleExistingDropdown"
                      :disabled="!availableTags.length"
                    >
                      <span class="truncate">
                        {{ selectedExistingLabel }}
                      </span>
                      <ChevronUpDownIcon class="w-4 h-4 text-zinc-400 flex-shrink-0" />
                    </button>

                    <!-- Dropdown -->
                    <div
                      v-if="isExistingDropdownOpen"
                      class="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg p-1"
                    >
                      <!-- Search -->
                      <div class="border-b border-zinc-200 p-2 mb-2">
                        <input
                          v-model="searchTerm"
                          type="text"
                          placeholder="Search..."
                          class="w-full rounded-md border border-zinc-200 px-2 py-2 text-xs
                                 focus:outline-none focus:ring-1 focus:ring-[#2D8CFF] focus:border-[#2D8CFF]"
                        />
                      </div>

                      <!-- List -->
                      <ul
                        class="chat-scroll max-h-56 overflow-y-auto scrollbar scrollbar-thumb-zinc-200 scrollbar-track-zinc-100 dark:scrollbar-track-zinc-500 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-hover:scrollbar-track-zinc-300 text-sm rounded-lg p-1"
                      >
                        <li
                          v-if="!filteredTags.length"
                          class="px-3 py-2 text-xs text-zinc-400"
                        >
                          No tags found.
                        </li>
                        <li
                          v-for="tag in filteredTags"
                          :key="tag.id || tag.tag_id"
                        >
                          <button
                            type="button"
                            class="flex w-full items-center px-3 py-2 text-left hover:bg-zinc-100"
                            @click="selectExistingTag(tag)"
                          >
                            <span class="truncate">{{ tag.name || tag.tag_name }}</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- New tag input -->
                <div v-if="selectedMode === 'new'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Enter a name for your tag
                  </label>
                  <input
                    v-model="newTagName"
                    type="text"
                    placeholder="VIP"
                    class="block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm
                           text-gray-900 shadow-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
                  />
                  <p class="mt-1 text-xs text-gray-500">
                    Tags are visible only to you. Enter at least 2 characters. You can use the English
                    alphabet, numbers, and underscores ("_").
                  </p>
                </div>

                <!-- Local error -->
                <p
                  v-if="localError"
                  class="text-xs text-red-600"
                >
                  {{ localError }}
                </p>
              </div>

              <!-- Footer buttons -->
              <div class="mt-8 sm:mt-10 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  class=" w-full justify-center rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1b6ad1] sm:ml-3 sm:w-auto disabled:bg-[#2D8CFF]/20 disabled:text-white disabled:cursor-not-allowed"
                  :disabled="!canApply"
                  @click="handleApply"
                >
                  Apply
                </button>
                <button
                  type="button"
                  class="mt-8 w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="handleClose"
                >
                  Cancel
                </button>
              </div>


            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  XMarkIcon,
  TagIcon,
  ChevronUpDownIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  availableTags: {
    type: Array,
    default: () => [],
  },
  memberName: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close', 'apply'])

const selectedMode = ref('') // 'existing' | 'new' | ''
const newTagName = ref('')
const existingTagId = ref(null)

const isExistingDropdownOpen = ref(false)
const isActionDropdownOpen = ref(false)

const searchTerm = ref('')
const localError = ref('')

const filteredTags = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()
  const base = props.availableTags || []

  if (!term) {
    return base
  }
  return base.filter((t) =>
    String(t.name || t.tag_name || '')
      .toLowerCase()
      .includes(term)
  )
})

const selectedExistingLabel = computed(() => {
  if (!props.availableTags?.length) {
    return 'No tags available'
  }
  if (!existingTagId.value) {
    return 'Select a tag'
  }
  const found = props.availableTags.find(
    (t) => (t.id ?? t.tag_id) === existingTagId.value
  )
  return found?.name || found?.tag_name || 'Select a tag'
})

const selectedActionLabel = computed(() => {
  if (selectedMode.value === 'existing') {
    return 'Select an existing tag'
  }
  if (selectedMode.value === 'new') {
    return 'Create a new tag'
  }
  return 'Select a tagging action'
})

const canApply = computed(() => {
  if (selectedMode.value === 'existing') {
    return !!existingTagId.value
  }
  if (selectedMode.value === 'new') {
    return newTagName.value.trim().length >= 2
  }
  return false
})

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      // Reset modal state each time it opens
      selectedMode.value = ''
      newTagName.value = ''
      existingTagId.value = null
      searchTerm.value = ''
      localError.value = ''
      isExistingDropdownOpen.value = false
      isActionDropdownOpen.value = false
    }
  }
)

function handleClose() {
  isExistingDropdownOpen.value = false
  isActionDropdownOpen.value = false
  emit('close')
}

function toggleActionDropdown() {
  isActionDropdownOpen.value = !isActionDropdownOpen.value
  if (isActionDropdownOpen.value) {
    isExistingDropdownOpen.value = false
  }
}

function selectActionMode(mode) {
  selectedMode.value = mode
  isActionDropdownOpen.value = false
  localError.value = ''

  // Clear dependent inputs when switching modes
  if (mode === 'existing') {
    newTagName.value = ''
  } else if (mode === 'new') {
    existingTagId.value = null
  }
}

function toggleExistingDropdown() {
  if (!props.availableTags?.length) return
  isExistingDropdownOpen.value = !isExistingDropdownOpen.value
  if (isExistingDropdownOpen.value) {
    isActionDropdownOpen.value = false
  }
}

function selectExistingTag(tag) {
  existingTagId.value = tag.id ?? tag.tag_id
  isExistingDropdownOpen.value = false
}

function handleApply() {
  localError.value = ''

  if (!canApply.value) {
    localError.value = 'Please complete the required fields before applying.'
    return
  }

  if (selectedMode.value === 'existing') {
    emit('apply', {
      mode: 'existing',
      tagId: existingTagId.value,
    })
  } else if (selectedMode.value === 'new') {
    emit('apply', {
      mode: 'new',
      tagName: newTagName.value.trim(),
    })
  }
}
</script>


<style scoped>

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}

</style>